import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { API } from "../App";
import BrandLogo from "../components/BrandLogo";
import { Printer, ArrowLeft, Share2, Phone, MapPin } from "lucide-react";

export default function PetPoster() {
  const { petId } = useParams();
  const [pet, setPet] = useState(null);
  const [owner, setOwner] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/pets`).then(r => {
      const p = r.data.find(x => x.pet_id === petId);
      if (!p) { setErr("Pet not found or you don't own this pet."); return; }
      setPet(p);
    }).catch(() => setErr("Could not load pet"));
    axios.get(`${API}/auth/me`).then(r => setOwner(r.data)).catch(() => {});
  }, [petId]);

  const share = async () => {
    const url = `${window.location.origin}/scan/${petId}`;
    if (navigator.share) {
      try { await navigator.share({ title: `${pet?.name}'s PetBond ID`, text: `If you find ${pet?.name}, scan or visit:`, url }); }
      catch {}
    } else {
      try { await navigator.clipboard.writeText(url); alert("Link copied!"); } catch {}
    }
  };

  if (err) return <div className="space-y-3" data-testid="poster-error"><Link to="/app/pets" className="text-sm font-bold text-brand inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Back</Link><div className="text-danger font-bold">{err}</div></div>;
  if (!pet) return <div className="text-ink-soft">Loading…</div>;

  const scanUrl = `${window.location.origin}/scan/${petId}`;
  const phone = owner?.phone || pet?.registration?.emergency_contact || "Contact via PetBond app";

  return (
    <div className="space-y-4" data-testid="pet-poster-page">
      <div className="flex items-center justify-between flex-wrap gap-2 print:hidden">
        <Link to="/app/pets" className="text-sm font-bold text-brand inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4"/> Back to my pets</Link>
        <div className="flex items-center gap-2">
          <button data-testid="poster-share" onClick={share} className="bg-surface border border-line rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 hover:border-brand hover:text-brand">
            <Share2 className="w-4 h-4"/> Share link
          </button>
          <button data-testid="poster-print" onClick={() => window.print()} className="bg-brand text-white rounded-full px-5 py-2.5 font-bold inline-flex items-center gap-2 hover:bg-brand-hover">
            <Printer className="w-4 h-4"/> Print / Save PDF
          </button>
        </div>
      </div>

      {/* A4 poster sheet — 210x297mm; use aspect ratio inside web preview */}
      <article id="poster-sheet" data-testid="poster-sheet" className="bg-white text-ink rounded-3xl border border-line shadow-soft mx-auto overflow-hidden print:shadow-none print:border-0 print:rounded-none" style={{ maxWidth: "780px", aspectRatio: "210 / 297" }}>
        <div className="h-full w-full flex flex-col p-8 print:p-10">
          {/* Header */}
          <header className="flex items-center justify-between border-b-4 border-brand pb-3">
            <BrandLogo variant="full" size="md" tagline={true} to={null}/>
            <div className="text-right">
              <div className="text-[10px] font-bold uppercase tracking-widest text-ink-soft">Reward · Inaam</div>
              <div className="font-heading text-2xl font-extrabold text-brand">₹ 2,000</div>
            </div>
          </header>

          {/* LOST headline */}
          <div className="mt-4 text-center">
            <div className="inline-block bg-brand text-white font-heading text-3xl font-extrabold px-6 py-1.5 rounded-full tracking-wider">IF FOUND</div>
            <h1 className="font-heading text-5xl sm:text-6xl font-extrabold mt-3 leading-none">
              Help me get<br/><span className="text-brand-blue">home</span> to <span className="text-brand">{pet.name}'s</span> family
            </h1>
          </div>

          {/* Pet photo */}
          <div className="mt-5 grid grid-cols-[1.2fr_1fr] gap-5 items-center">
            <div className="relative">
              <img src={pet.photo} alt={pet.name} className="w-full h-64 object-cover rounded-3xl border-4 border-brand-blue"/>
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-brand-blue text-white rounded-full px-4 py-1 text-xs font-bold uppercase tracking-wider">{pet.species}</div>
            </div>
            <div>
              <div className="font-heading text-4xl font-extrabold text-brand">{pet.name}</div>
              <div className="text-base font-bold text-ink-soft mt-1">{pet.breed}</div>
              <div className="text-sm text-ink-soft">{pet.age_years} years · {pet.species}</div>
              {pet.registration?.microchip_id && (
                <div className="mt-3 text-xs">
                  <div className="font-bold uppercase tracking-wider text-ink-soft">Microchip</div>
                  <div className="font-mono font-bold">{pet.registration.microchip_id}</div>
                </div>
              )}
            </div>
          </div>

          {/* QR + contact */}
          <div className="mt-6 bg-surface-muted rounded-3xl p-5 flex items-center gap-5">
            <div className="bg-white p-2 rounded-2xl shadow-soft shrink-0">
              <QRCodeSVG value={scanUrl} size={130} level="M" includeMargin={false}/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading text-xl font-extrabold leading-tight">📱 Scan or call to return me</div>
              <div className="text-sm text-ink-soft mt-1">Open the camera on your phone, point at the QR code — you'll get my owner's contact instantly.</div>
              <div className="mt-3 flex items-center gap-2 text-base font-bold">
                <Phone className="w-4 h-4 text-brand-green"/> <span className="font-mono">{phone}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="mt-auto pt-4 text-center border-t border-line">
            <div className="text-[11px] text-ink-soft">No reward needed if returned safely. Pets are family.</div>
            <div className="text-[10px] text-ink-soft mt-1 font-bold tracking-widest uppercase">
              <span className="text-brand">Pet</span><span className="text-brand-blue">Bond</span> · petbond.app/scan/{petId.slice(-8)}
            </div>
          </footer>
        </div>
      </article>

      <div className="text-xs text-ink-soft text-center max-w-md mx-auto print:hidden">
        💡 Tip: Print 5-10 copies at home, laminate them, and stick them on lampposts in a 1km radius of your home. Every poster doubles as a PetBond billboard 🐾
      </div>

      <style>{`
        @media print {
          body { background: #fff !important; }
          .print\\:hidden { display: none !important; }
          #poster-sheet { box-shadow: none !important; border: 0 !important; max-width: 100% !important; width: 100% !important; height: 100vh !important; }
          @page { size: A4; margin: 0; }
        }
      `}</style>
    </div>
  );
}
